import { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import style from "../../Admin/admin.module.css";
import { Button, LabelBox } from "../../../components";
import { colors } from "../../../theme/colors";
import { CustomTable, StatusBadge } from "../../../components/DataTable";
import { CustomActionMenu } from "../../../components/Menu";
import {
  useGetUserRolesQuery,
  useGetUsersListQuery,
  useUpdateUserMutation,
} from "../../../services/api";
import {
  ConfirmationDialog,
  DetailsDialog,
  InputDialog,
} from "../../../components/Dialog";
import { Input } from "../../../components/UserInput";
import { updateStatusSchema } from "../../../yup";
import { getErrorMessage } from "../../../utils/common";
import { formatCourierName } from "../../Courier/utils";
import {
  buildUserActions,
  renderCellText,
  resetDeactivateReasonField,
  useStatusDialogs,
} from "../userShared";
import { DEFAULT_COURIER_ROLE_ID } from "../../../utils/constants";

/** Form values inferred from the update status schema. */
type FormState = typeof updateStatusSchema.__outputType;
/** Action identifiers for courier rows. */
type CourierAction = "view" | "edit" | "logs" | "deactivate" | "activate";

/** Props for the courier list tab. */
type CourierTabProps = {
  searchKey?: string;
  onClearSearch?: () => void;
};

/**
 * Renders the courier list tab with search and status actions.
 *
 * Args:
 *   searchKey: Optional search query.
 *
 * Returns:
 *   JSX.Element: Courier list tab content.
 */
const CourierTab = ({ searchKey, onClearSearch }: CourierTabProps) => {
  const { id } = useParams();
  const laboratoryId = Number.isFinite(Number(id)) ? Number(id) : undefined;
  const navigate = useNavigate();
  const [updateUser] = useUpdateUserMutation();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [selectedCourier, setSelectedCourier] = useState<Users | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const {
    confirmOpen,
    reasonOpen,
    logsOpen,
    openConfirm,
    closeConfirm,
    openReason,
    closeReason,
    openLogs,
    closeLogs,
  } = useStatusDialogs();

  const { data: rolesData } = useGetUserRolesQuery({
    is_active: true,
    is_detailed: true,
  });

  const courierRoleId = useMemo(
    () =>
      rolesData?.items?.find(
        (role: { name: string }) => role?.name === "Courier"
      )?.id,
    [rolesData]
  );

  const [queryParam, setQueryParam] = useState({
    search_key: searchKey,
    order_by_columns: "surname",
    order: "Asc",
    is_detailed: true,
    laboratory_id: laboratoryId,
    user_role_id: courierRoleId ?? DEFAULT_COURIER_ROLE_ID,
  });

  const {
    data: courierData,
    refetch: refetchCouriers,
    isFetching,
  } = useGetUsersListQuery(queryParam);

  useEffect(() => {
    if (courierRoleId) {
      setQueryParam((prevParams) => ({
        ...prevParams,
        user_role_id: courierRoleId,
      }));
    }
  }, [courierRoleId]);

  useEffect(() => {
    if (
      typeof laboratoryId === "number" &&
      queryParam.laboratory_id !== laboratoryId
    ) {
      setQueryParam((prevParams) => ({
        ...prevParams,
        laboratory_id: laboratoryId,
      }));
    }
  }, [laboratoryId, queryParam.laboratory_id]);

  useEffect(() => {
    setQueryParam((prevParams) => ({
      ...prevParams,
      search_key: searchKey === "" ? undefined : searchKey,
      offset: 0,
    }));
  }, [searchKey]);

  const handleMenu = (value: CourierAction, courier: Users) => {
    setSelectedCourier(courier);
    switch (value) {
      case "deactivate":
      case "activate":
        openConfirm();
        break;
      case "view":
        navigate(`/couriers/details/${courier?.id}`, {
          state: {
            source: "USER_DETAILS",
            userType: "COURIER",
          },
        });
        break;
      case "edit":
        navigate(`/couriers/edit/${courier?.id}`, {
          state: { userType: "COURIER" },
        });
        break;
      case "logs":
        openLogs();
        break;
      default:
        break;
    }
  };

  const fields = [
    {
      id: "surname",
      label: "Name",
      flex: 2.5,
      sortable: true,
      type: "name",
      render: (item: Users) =>
        renderCellText(formatCourierName(item), { className: style.cell }),
    },
    {
      id: "mail",
      label: "Email",
      flex: 2.5,
      render: (item: Users) =>
        renderCellText(`${item.mail ?? ""}`, { className: style.cell }),
    },
    {
      id: "is_active",
      label: "Status",
      type: "badge",
      flex: 1.3,
      sortable: true,
      render: (item: Users) => <StatusBadge isActive={item.is_active ?? false} />,
    },
    {
      id: "logo_s3_key_name",
      label: "Actions",
      type: "action",
      render: (item: Users) => (
        <CustomActionMenu<Users, CourierAction>
          item={item}
          isActive={item?.is_active ?? false}
          handleChange={handleMenu}
          actions={buildUserActions<CourierAction>([
            "view",
            ...(item.is_active ? (["edit"] as CourierAction[]) : []),
            "logs",
            item.is_active ? "deactivate" : "activate",
          ])}
        />
      ),
    },
  ];

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm<FormState>({
    resolver: yupResolver(updateStatusSchema),
  });

  /**
   * Updates courier activation status with an optional deactivation reason.
   *
   * Args:
   *   values: Validated form payload.
   *
   * Returns:
   *   Promise<void>: Resolves after status update and refetch.
   */
  const onSubmit = async (values: FormState) => {
    if (!selectedCourier?.id) {
      setMessage({
        type: "error",
        text: "No courier selected for status update.",
      });
      return;
    }
    try {
      setIsLoading(true);
      const { deactivate_reason } = values;
      await updateUser({
        id: selectedCourier.id,
        is_active: !selectedCourier.is_active,
        deactivate_reason: deactivate_reason ?? "",
      }).unwrap();
      setMessage({
        type: "success",
        text: "Status updated successfully.",
      });
      closeReason();
      resetDeactivateReasonField(setValue, clearErrors);
      await refetchCouriers();
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Something went wrong!"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {message && (
        <Box mt={2}>
          <LabelBox
            boxProps={{ height: 50, display: "flex", alignItems: "center" }}
            data={message}
            onClose={() => setMessage(null)}
          />
        </Box>
      )}
      <Box
        style={{
          maxHeight: "calc(100vh - 195px)",
          overflow: "auto",
          marginTop: "10px",
        }}
      >
        <CustomTable<Users>
          fields={fields}
          paginationRequired={true}
          data={courierData?.items ?? []}
          loading={isFetching}
          onDoubleClick={(courier: Users) => {
            navigate(`/couriers/details/${courier?.id}`, {
              state: {
                source: "USER_DETAILS",
                userType: "COURIER",
              },
            });
          }}
          defaultOrderBy={"surname"}
          searchTerm={searchKey}
          onClearSearch={onClearSearch}
        />
      </Box>
      {confirmOpen && (
        <ConfirmationDialog
          visible={confirmOpen}
          message={`Are you sure you want to ${
            selectedCourier?.is_active ? "deactivate " : "activate "
          }"${formatCourierName(selectedCourier)}"?`}
          onClose={() => {
            closeConfirm();
          }}
          onConfirm={() => {
            closeConfirm();
            if (selectedCourier?.is_active) {
              openReason();
            } else {
              onSubmit({ deactivate_reason: "" });
            }
          }}
        />
      )}
      {reasonOpen && (
        <InputDialog
          visible={reasonOpen}
          onClose={() => {
            closeReason();
            resetDeactivateReasonField(setValue, clearErrors);
          }}
        >
          <Box
            component="form"
            sx={{ marginX: "20px" }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Typography
              variant="h2"
              component="div"
              fontSize={16}
              fontWeight={700}
              color={colors.modalHeading}
              marginBottom={"10px"}
              sx={{ display: "flex", flexDirection: "row" }}
            >
              {"Enter the reason for deactivation"}
              <Typography
                color={"red"}
                fontSize={16}
                fontWeight={700}
                sx={{ marginLeft: "5px", marginTop: "-5px" }}
              >
                {"*"}
              </Typography>
            </Typography>
            <Input
              placeholder=""
              name="deactivate_reason"
              required
              register={register}
              multiline
              rows={3}
              errors={errors}
              sx={{
                width: "100%",
                margin: "1px",
                borderWidth: "1px",
              }}
            />
            <Box className={style.horizontalButtons} sx={{ paddingX: "50px" }}>
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  backgroundColor: colors.white,
                  ":hover": { backgroundColor: colors.white },
                }}
                variant="outlined"
                onClick={() => {
                  closeReason();
                  resetDeactivateReasonField(setValue, clearErrors);
                }}
                label={"Cancel"}
              />
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  ":hover": { backgroundColor: colors.primary },
                }}
                variant="contained"
                type="submit"
                label={"Submit"}
                loading={isLoading}
              />
            </Box>
          </Box>
        </InputDialog>
      )}
      {logsOpen && (
        <DetailsDialog
          visible={logsOpen}
          details={selectedCourier}
          onClose={() => {
            closeLogs();
          }}
          onConfirm={() => {
            closeLogs();
          }}
        />
      )}
    </>
  );
};

export default CourierTab;
